/**
 * Project Sentinel — governed fix proposals for slipping projects.
 *
 * ClickUp shows you a red dashboard; this module proposes what to DO about
 * it. It runs the already-built dependency engine (`crm/taskGraph.ts` —
 * topo order, critical path, slip detection) over a task snapshot and turns
 * each slip into a concrete, governed `AgentAction` plus a human-readable
 * reason:
 *
 *   - an overdue blocker holding up dependents → ONE de-duped `create_task`
 *     "Unblock …" proposal, assigned to whoever holds the blocker, whose
 *     reason names every stuck dependent
 *   - a critical-path task that is itself overdue while still blocked →
 *     an urgent `create_task` escalation proposal
 *   - a dependency cycle → a `create_task` proposal to untangle it (the
 *     graph engine flags cycles instead of throwing; the sentinel surfaces
 *     them as work, never a crash)
 *
 * PURE by construction: it takes a `ProjectTask[]` snapshot (plus an
 * optional injected `today` for determinism) and RETURNS proposals. It
 * queues nothing and writes nothing — callers route the proposals through
 * the same confirm-each-write approvals flow every other agent uses
 * (`create_task` is reversible via `runActionWithUndo`). Graph logic is
 * reused from `taskGraph`, never reimplemented.
 */

import { analyzeTaskGraph } from '../crm/taskGraph';
import type { SlipReason } from '../crm/taskGraph';
import type { ProjectTask } from '../db/types';
import type { AgentAction, CreateTaskAction } from './types';

/**
 * Action kinds the sentinel is allowed to propose — all reversible
 * (undoable by `runActionWithUndo` after approval). Exported so tests and
 * future producers can assert the guarantee instead of trusting it.
 */
export const REVERSIBLE_KINDS: ReadonlySet<AgentAction['kind']> = new Set([
  'create_task',
  'schedule_follow_up',
]);

/** One governed fix: the proposed action + why the sentinel proposes it. */
export interface SentinelProposal {
  action: AgentAction;
  /** Human-readable justification shown on the approval card. */
  reason: string;
}

/** Oxford-free human list: "a", "a and b", "a, b and c". */
function humanList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Analyze the task graph and propose governed, reversible fixes for every
 * slip. Pure: returns the proposals; performs no queueing and no writes.
 */
export function proposeProjectFixes(
  tasks: ProjectTask[],
  today?: string,
): SentinelProposal[] {
  const analysis = analyzeTaskGraph(tasks, today);
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const onCriticalPath = new Set(analysis.criticalPath.ids);
  const proposals: SentinelProposal[] = [];

  // -- (1) Overdue blockers → one de-duped unblock proposal per blocker ----
  // Collect every dependent stuck behind each overdue blocker so the single
  // proposal's reason shows the full blast radius.
  const stuckBehind = new Map<string, { dependents: string[]; detail: string }>();
  for (const slip of analysis.slips) {
    const dependent = byId.get(slip.taskId);
    if (!dependent) continue;
    for (const reason of slip.reasons) {
      if (reason.kind !== 'blocker-overdue' || !reason.blockerId) continue;
      const entry = stuckBehind.get(reason.blockerId) ?? {
        dependents: [],
        detail: reason.detail,
      };
      entry.dependents.push(dependent.title);
      stuckBehind.set(reason.blockerId, entry);
    }
  }
  for (const [blockerId, { dependents, detail }] of stuckBehind) {
    const blocker = byId.get(blockerId);
    if (!blocker) continue;
    const action: CreateTaskAction = {
      kind: 'create_task',
      title: `Unblock: resolve overdue blocker "${blocker.title}"`,
      priority: 'high',
      // Land the unblock work with whoever already holds the blocker.
      assignee: blocker.assignee,
      dueDate: today ?? null,
      ...(blocker.accountId
        ? { accountId: blocker.accountId, accountName: blocker.accountName }
        : {}),
    };
    proposals.push({
      action,
      reason: `${detail} — it is holding up ${humanList(
        dependents.map((d) => `"${d}"`),
      )}.`,
    });
  }

  // -- (2) Critical-path task itself overdue while blocked → escalation ----
  for (const slip of analysis.slips) {
    if (!onCriticalPath.has(slip.taskId)) continue;
    const overdue = slip.reasons.find(
      (r: SlipReason) => r.kind === 'overdue-blocked',
    );
    if (!overdue) continue;
    const slipped = byId.get(slip.taskId);
    if (!slipped) continue;
    const action: CreateTaskAction = {
      kind: 'create_task',
      title: `Escalate: critical-path task "${slipped.title}" is slipping`,
      priority: 'urgent',
      assignee: slipped.assignee,
      dueDate: today ?? null,
      ...(slipped.accountId
        ? { accountId: slipped.accountId, accountName: slipped.accountName }
        : {}),
    };
    proposals.push({
      action,
      reason: `"${slipped.title}" is on the critical path and slipping: ${overdue.detail.toLowerCase()}. Every downstream task inherits the delay.`,
    });
  }

  // -- (3) Dependency cycle → propose untangling it (flag, never throw) ----
  if (analysis.hasCycle) {
    const names = analysis.cycleIds
      .map((id) => byId.get(id)?.title)
      .filter((t): t is string => Boolean(t));
    const action: CreateTaskAction = {
      kind: 'create_task',
      title: 'Untangle dependency cycle in the task graph',
      priority: 'high',
      assignee: null,
      dueDate: today ?? null,
    };
    proposals.push({
      action,
      reason: `Dependency cycle detected between ${humanList(
        names.map((n) => `"${n}"`),
      )} — these tasks block each other, so none can start until an edge is removed.`,
    });
  }

  return proposals;
}
